import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from "three-mesh-bvh";
import { computeVolume, computeSampledVolume } from "@/lib/meshVolume";
import { Focus, Expand } from "lucide-react";

// Enable BVH-accelerated raycasting on all Three.js meshes.
THREE.Mesh.prototype.raycast = acceleratedRaycast;
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

const REF_LIBRARY = {
  quarter: { label: "US Quarter", dia: 24.26, height: 1.75, color: 0xc8a961 },
  can: { label: "Soda Can", dia: 66, height: 122, color: 0xd94848 },
  bottle: { label: "2L Bottle", dia: 110, height: 330, color: 0x60a5fa },
};

/**
 * StlViewer — an embedded 3D preview for print-quote uploads.
 *
 * Supports binary/ASCII STL and OBJ from uploads or the existing catalog.
 *
 * Exposes an imperative `loadFile(file)` handle so the parent form can:
 *   1. Read the uploaded File.
 *   2. Get back { volume_mm3, bbox_mm: {x,y,z}, format }.
 *
 * Volume math uses **ray-cast column sampling** by default — for each cell of
 * a fine XZ grid over the mesh bounds, we shoot a vertical ray, pair the
 * intersections (enter/exit), and sum the "filled column" length × cell area.
 * This is the same technique real slicers use, so the resulting mm³ matches
 * true resin consumption even on hollow-interior or non-manifold meshes.
 * The signed-tetrahedron sum is kept as a fallback if sampling fails.
 */
export const StlViewer = forwardRef(function StlViewer(
  { modelFile, onLoaded, onError, onLabelPositions, height = 460, bedMm = { x: 218, y: 122, z: 220 }, references = {} },
  ref
) {
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const meshRef = useRef(null);
  const bedGroupRef = useRef(null);
  const refsGroupRef = useRef(null);
  const tightBoxRef = useRef(null);      // wireframe hugging the loaded mesh
  const tightLabelsRef = useRef(null);   // sprite dim readouts on the tight box
  const onLabelPositionsRef = useRef(onLabelPositions);
  const animRef = useRef(0);
  const loadRequestRef = useRef(0);
  const handleRef = useRef(null);
  const modelFocusRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("idle"); // 'idle' | 'loading' | 'loaded' | 'unsupported' | 'error'
  const [format, setFormat] = useState(null);

  // Keep the label-positions callback in a ref so the animation loop
  // always sees the latest version without needing to re-init the scene.
  useEffect(() => { onLabelPositionsRef.current = onLabelPositions; }, [onLabelPositions]);

  // Init three.js once
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const width = mount.clientWidth;
    const h = Math.max(1, mount.clientHeight);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0d14);

    // Build the printer bed as a wireframe volume so users see the model's
    // relationship to the Elegoo Saturn print envelope. We use the
    // ChiTuBox / printer convention throughout: X = left/right (width),
    // Y = front-back (depth), Z = up (print stacking axis / height).
    // Three.js itself is Y-up so we route: printer-Z → three-Y (up),
    // printer-Y → three-Z (into screen). The visual result matches what
    // the physical printer + ChiTuBox show.
    const bedX = bedMm.x;      // width  (three X)
    const bedZ = bedMm.y;      // depth  (three Z)
    const bedY = bedMm.z;      // height (three Y)

    const bedGroup = new THREE.Group();

    // Floor grid sized to the actual bed footprint
    const gridSize = Math.max(bedX, bedZ);
    const grid = new THREE.GridHelper(gridSize, 20, 0xf472b6, 0x1a1e2b);
    grid.material.opacity = 0.35;
    grid.material.transparent = true;
    bedGroup.add(grid);

    // Solid semi-transparent floor tile (exact bed footprint) so the user sees
    // the true build-plate outline vs the wider grid.
    const plateGeo = new THREE.PlaneGeometry(bedX, bedZ);
    const plateMat = new THREE.MeshBasicMaterial({
      color: 0x0e1220,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
    });
    const plate = new THREE.Mesh(plateGeo, plateMat);
    plate.rotation.x = -Math.PI / 2;
    plate.position.y = 0.05;   // above the grid to avoid z-fighting
    bedGroup.add(plate);

    // Wireframe box for the entire build volume
    const boxGeo = new THREE.BoxGeometry(bedX, bedY, bedZ);
    const edges = new THREE.EdgesGeometry(boxGeo);
    const line = new THREE.LineSegments(
      edges,
      new THREE.LineBasicMaterial({ color: 0xf472b6, transparent: true, opacity: 0.7 })
    );
    line.position.set(0, bedY / 2, 0);
    bedGroup.add(line);

    // Faint tinted volume so the printable envelope reads as a "shape" not
    // just a line frame.
    const volMat = new THREE.MeshBasicMaterial({
      color: 0xf472b6,
      transparent: true,
      opacity: 0.03,
      depthWrite: false,
    });
    const vol = new THREE.Mesh(boxGeo, volMat);
    vol.position.set(0, bedY / 2, 0);
    bedGroup.add(vol);

    // Corner tick marks at Z=height so users can spot the ceiling at a glance
    const cornerMat = new THREE.LineBasicMaterial({ color: 0xf472b6, transparent: true, opacity: 0.9 });
    const tick = 12;
    const hx = bedX / 2, hz = bedZ / 2;
    for (const [cx, cz] of [[hx, hz], [-hx, hz], [hx, -hz], [-hx, -hz]]) {
      const pts = [
        new THREE.Vector3(cx, bedY, cz), new THREE.Vector3(cx - Math.sign(cx) * tick, bedY, cz),
        new THREE.Vector3(cx, bedY, cz), new THREE.Vector3(cx, bedY, cz - Math.sign(cz) * tick),
        new THREE.Vector3(cx, bedY, cz), new THREE.Vector3(cx, bedY - tick, cz),
      ];
      bedGroup.add(new THREE.LineSegments(
        new THREE.BufferGeometry().setFromPoints(pts), cornerMat));
    }

    scene.add(bedGroup);
    bedGroupRef.current = bedGroup;

    // Cinematic-ish lighting: key + fill + rim
    scene.add(new THREE.AmbientLight(0xffffff, 0.35));
    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    key.position.set(200, 250, 200);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0x88ccff, 0.4);
    fill.position.set(-200, 150, -100);
    scene.add(fill);
    const rim = new THREE.DirectionalLight(0xf472b6, 0.4);
    rim.position.set(0, -200, -150);
    scene.add(rim);

    const camera = new THREE.PerspectiveCamera(45, width / h, 0.1, 5000);
    // Frame the whole bed by default so the empty state still communicates scale
    const frameDist = Math.max(bedX, bedY, bedZ) * 1.9 + 80;
    camera.position.set(frameDist * 0.9, frameDist * 0.7, frameDist);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.domElement.dataset.testid = "stl-viewer-canvas";
    renderer.domElement.setAttribute("aria-label", "Interactive 3D print model");
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, h);
    mount.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    // Reusable vector for edge-midpoint projection each frame — avoids
    // per-frame allocation churn.
    const projVec = new THREE.Vector3();

    const animate = () => {
      animRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);

      // Broadcast the screen-space centre points of the three tight-box
      // edges (width X, depth Z, height Y) so the parent can float a
      // clickable dimension label on each one. Sends `null` when no
      // model is loaded so the labels hide.
      const cb = onLabelPositionsRef.current;
      if (cb && meshRef.current && tightBoxRef.current) {
        const mesh = meshRef.current;
        const scaleV = mesh.scale.x;
        // Raw un-scaled box dims came from the initial commit; the tight
        // box shares the same scale as the mesh.
        const boxDims = tightBoxRef.current.userData.rawSize || null;
        if (boxDims) {
          const dx = boxDims.x * scaleV;
          const dy = boxDims.y * scaleV;
          const dz = boxDims.z * scaleV;
          // Mesh sits with base at three-Y = 0 and centered on X/Z.
          // Midpoints of each axis edge (front-bottom face of the box):
          //   X (width) edge : (0, 0, dz/2)
          //   Z (depth) edge : (-dx/2, 0, 0)
          //   Y (height) edge: (-dx/2, dy/2, dz/2)
          const anchors = {
            x: new THREE.Vector3(0, 0, dz / 2),
            z: new THREE.Vector3(-dx / 2, 0, 0),
            y: new THREE.Vector3(-dx / 2, dy / 2, dz / 2),
          };
          const canvas = renderer.domElement;
          const bw = canvas.clientWidth, bh = canvas.clientHeight;
          const out = {};
          for (const [k, v] of Object.entries(anchors)) {
            projVec.copy(v).project(camera);
            out[k] = {
              screenX: ((projVec.x + 1) / 2) * bw,
              screenY: ((-projVec.y + 1) / 2) * bh,
              mm: k === "x" ? dx : k === "y" ? dy : dz,
              visible: projVec.z < 1,
            };
          }
          cb(out);
        } else {
          cb(null);
        }
      } else if (cb) {
        cb(null);
      }
    };
    animate();

    // Responsive resize
    const ro = new ResizeObserver(() => {
      const w = Math.max(1, mount.clientWidth);
      const currentHeight = Math.max(1, mount.clientHeight);
      renderer.setSize(w, currentHeight);
      camera.aspect = w / currentHeight;
      camera.updateProjectionMatrix();
      if (modelFocusRef.current) handleRef.current?.focusModel();
    });
    ro.observe(mount);

    rendererRef.current = renderer;
    sceneRef.current = scene;
    cameraRef.current = camera;
    controlsRef.current = controls;

    // Reference-object group — a scale bench next to the print bed. Items
    // populated by the `references` prop effect below.
    const refsGroup = new THREE.Group();
    scene.add(refsGroup);
    refsGroupRef.current = refsGroup;

    return () => {
      loadRequestRef.current += 1;
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement && renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [bedMm.x, bedMm.y, bedMm.z]);

  // ── Reference objects (scale bench) ──────────────────────────────
  // Physical everyday items rendered next to the print bed so the user has
  // instant intuition about the model's true size. All dims in mm.

  // Rebuild the reference lineup whenever the user toggles one on/off. They
  // sit in a row on the +X side of the print bed so they never overlap the
  // model, and each labels its own footprint on the plate.
  useEffect(() => {
    const grp = refsGroupRef.current;
    if (!grp) return;
    // Clear existing
    while (grp.children.length) {
      const c = grp.children.pop();
      c.geometry?.dispose();
      c.material?.dispose();
    }
    const bedHalfX = (bedMm.x || 218) / 2;
    let cursor = bedHalfX + 40;   // 40mm gap off the bed's +X edge
    for (const [key, spec] of Object.entries(REF_LIBRARY)) {
      if (!references[key]) continue;
      const geo = new THREE.CylinderGeometry(spec.dia / 2, spec.dia / 2, spec.height, 40);
      const mat = new THREE.MeshStandardMaterial({
        color: spec.color,
        transparent: true,
        opacity: 0.85,
        roughness: 0.6,
        metalness: 0.15,
      });
      const cyl = new THREE.Mesh(geo, mat);
      cyl.position.set(cursor + spec.dia / 2, spec.height / 2, 0);
      grp.add(cyl);
      // Wireframe outline so the reference reads as a "ghost" and doesn't
      // steal focus from the real model.
      const wire = new THREE.LineSegments(
        new THREE.EdgesGeometry(geo),
        new THREE.LineBasicMaterial({ color: spec.color, transparent: true, opacity: 0.8 })
      );
      wire.position.copy(cyl.position);
      grp.add(wire);
      cursor += spec.dia + 30;   // 30mm gap between reference items
    }
    // No reframe here — respect the user's current camera position. If a
    // newly-enabled reference falls outside the frame, they can orbit/zoom
    // out manually. Auto-reframing was surprising when triggered mid-edit.
  }, [references, bedMm.x]);

  // Given a geometry with known units (assumed mm — STL/OBJ have no explicit
  // units so we trust the source), plate it, frame the camera, and return
  // the geometric report to the parent.
  //
  // No rotation and no axis remapping of the geometry itself — the file
  // displays exactly as loaded. We DO remap the reported dimensions so they
  // match the printer bed's semantic axes (bed.x=width, bed.y=depth,
  // bed.z=height) as drawn by the wireframe:
  //   printer X (width, 218)  ← three-X  (size.x)
  //   printer Y (depth, 122)  ← three-Z  (size.z)   ← into screen
  //   printer Z (height, 220) ← three-Y  (size.y)   ← up
  // This way the model's tight bounding box lines up visually AND numerically
  // with the print envelope wireframe — no more "the outline says it fits
  // but the number says it's too big" surprises.
  const commitGeometry = (geometry, formatLabel) => {
    if (!sceneRef.current || !geometry.getAttribute("position")?.count) {
      geometry.dispose();
      throw new Error("This file has no renderable model geometry.");
    }
    // Clean up previous mesh + tight box
    if (meshRef.current) {
      sceneRef.current.remove(meshRef.current);
      meshRef.current.geometry?.dispose();
      meshRef.current.material?.dispose();
      meshRef.current = null;
    }
    if (tightBoxRef.current) {
      sceneRef.current.remove(tightBoxRef.current);
      tightBoxRef.current.geometry?.dispose?.();
      tightBoxRef.current.material?.dispose?.();
      tightBoxRef.current = null;
    }

    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    const bbox = geometry.boundingBox;
    const size = new THREE.Vector3();
    bbox.getSize(size);
    if (![size.x, size.y, size.z].every(Number.isFinite) || Math.max(size.x, size.y, size.z) <= 0) {
      geometry.dispose();
      throw new Error("This model contains invalid dimensions.");
    }
    const center = new THREE.Vector3();
    bbox.getCenter(center);

    // Sit the model's lowest point on the print plate (three.Y = 0).
    geometry.translate(-center.x, -bbox.min.y, -center.z);

    const material = new THREE.MeshStandardMaterial({
      color: 0xd8b6ff,
      metalness: 0.1,
      roughness: 0.55,
      flatShading: false,
    });
    const mesh = new THREE.Mesh(geometry, material);
    sceneRef.current.add(mesh);
    meshRef.current = mesh;

    // ── Tight bounding wireframe hugging the mesh ─────────────────
    // Uses a unit BoxGeometry sized to the raw dims and re-scaled together
    // with the mesh in setScale(). Cyan to distinguish from the pink bed.
    buildTightBox(size);

    // First-time framing on load — reused framing helper handles the actual
    // math so scale changes later can call the same code.
    reframe(size, 1);

    const volume = computeVolume(geometry);
    // Ray-cast column sampling is the "true" resin volume for watertight
    // meshes and it correctly picks up hollow interiors. BUT it can
    // significantly under-count on non-manifold / multi-shell / open-surface
    // exports (Meshy AI, Sketchfab dumps, etc.) where the enter/exit ray
    // pairing fails. Signed-tetrahedron is exact for closed manifolds and
    // deterministic for weird ones — we take the MAX of the two so we
    // never under-quote a customer. Real prints round up, not down.
    let sampled = 0;
    try {
      sampled = computeSampledVolume(geometry);
    } catch (err) {
      console.warn("[StlViewer] Sampled-volume failed, falling back to mesh volume:", err);
    }
    const effectiveVolume = Math.max(sampled || 0, volume || 0);
    // Method label reflects which one actually won.
    let volumeMethod;
    if (sampled > 0 && volume > 0) {
      // If they differ by more than 20%, flag the disagreement so the admin
      // can eyeball weird meshes in the audit view.
      const ratio = Math.max(sampled, volume) / Math.max(1e-6, Math.min(sampled, volume));
      volumeMethod = ratio > 1.2
        ? (sampled > volume ? 'ray_sampled_high' : 'mesh_tetrahedron_high')
        : 'agreed';
    } else if (sampled > 0) {
      volumeMethod = 'ray_sampled';
    } else {
      volumeMethod = 'mesh_tetrahedron';
    }

    // No rotation of the geometry itself — the file displays exactly as
    // loaded. Reported dimensions use the ChiTuBox / printer convention:
    //   X = left/right (width)   ← three-X (size.x)
    //   Y = front-back (depth)   ← three-Z (size.z)
    //   Z = up / height          ← three-Y (size.y)
    // Bed check on the backend uses the same convention so the tight
    // bounding box + bed wireframe + numeric readout all agree.
    const report = {
      volume_mm3: effectiveVolume,           // primary — used by the quote
      mesh_volume_mm3: volume,               // signed-tetrahedron reference
      sampled_volume_mm3: sampled,           // ray-cast (0 if fallback)
      volume_method: volumeMethod,
      bbox_mm: { x: size.x, y: size.z, z: size.y },
      format: formatLabel,
    };
    setStatus("loaded");
    setFormat(formatLabel);
    onLoaded && onLoaded(report);
    return report;
  };

  // Build (or rebuild) the tight bounding wireframe around the loaded mesh.
  // Stored as a group so we can update its scale in setScale() without
  // rebuilding the geometry every frame.
  const buildTightBox = (rawSize) => {
    if (!sceneRef.current || !rawSize) return;
    if (tightBoxRef.current) {
      sceneRef.current.remove(tightBoxRef.current);
      tightBoxRef.current.traverse((c) => {
        c.geometry?.dispose?.();
        c.material?.dispose?.();
      });
      tightBoxRef.current = null;
    }
    // Unit box that we'll scale to the raw dims — the outer group's scale
    // then multiplies by the current user scale in setScale().
    const geo = new THREE.BoxGeometry(rawSize.x, rawSize.y, rawSize.z);
    const edges = new THREE.EdgesGeometry(geo);
    geo.dispose();
    const wire = new THREE.LineSegments(
      edges,
      new THREE.LineBasicMaterial({ color: 0x22d3ee, transparent: true, opacity: 0.9 })
    );
    // The mesh has already been centered on X/Z with base at Y=0. So the
    // tight box needs to sit with its center at (0, rawSize.y/2, 0).
    wire.position.set(0, rawSize.y / 2, 0);
    const grp = new THREE.Group();
    grp.add(wire);
    grp.userData.rawSize = { x: rawSize.x, y: rawSize.y, z: rawSize.z };
    sceneRef.current.add(grp);
    tightBoxRef.current = grp;
  };

  // Pull the camera back so it can comfortably show:
  //   • the printer bed
  //   • the model at its current effective scale (even 3×+ oversize for
  //     piecemeal / cut-and-glue work)
  //   • any reference objects lined up alongside
  // Given max envelope dim `d`, we place the camera at ~3× that distance —
  // 3× the previous "just fit" default — so the useful viewing frustum
  // extends well past the printer envelope on every axis.
  const rawModelSizeRef = useRef(null);
  const currentScaleRef = useRef(1);
  const reframe = (rawSize, scale) => {
    if (rawSize) rawModelSizeRef.current = rawSize;
    if (Number.isFinite(scale) && scale > 0) currentScaleRef.current = scale;
    const s = rawModelSizeRef.current;
    const sc = currentScaleRef.current || 1;
    const scaledMax = s ? Math.max(s.x, s.y, s.z) * sc : 0;
    const bedMax = Math.max(bedMm.x, bedMm.y, bedMm.z);
    const refMax = Math.max(
      references?.quarter ? 24.26 : 0,
      references?.can ? 122 : 0,
      references?.bottle ? 330 : 0,
    );
    const refRowWidth = (references?.quarter ? 24.26 + 30 : 0) + (references?.can ? 66 + 30 : 0) + (references?.bottle ? 110 + 30 : 0);
    const envelope = Math.max(scaledMax, bedMax, refMax, bedMm.x + refRowWidth + 80);
    // 3× headroom — user asked for this explicitly so oversized/exploded
    // models remain in frame.
    const camDist = envelope * 3 + 60;
    if (!cameraRef.current) return;
    cameraRef.current.position.set(camDist * 0.9, camDist * 0.7, camDist);
    cameraRef.current.far = Math.max(5000, camDist * 20);
    cameraRef.current.updateProjectionMatrix();
    const focusY = Math.max(bedMm.z / 2, (s?.y || 0) * sc / 2);
    cameraRef.current.lookAt(0, focusY, 0);
    controlsRef.current?.target.set(0, focusY, 0);
    controlsRef.current?.update();
  };

  handleRef.current = {
    async loadFile(file) {
      if (!file) return null;
      const requestId = ++loadRequestRef.current;
      setLoading(true);
      setStatus("loading");
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      try {
        if (ext === "stl") {
          const buf = await file.arrayBuffer();
          if (requestId !== loadRequestRef.current) return null;
          const geo = new STLLoader().parse(buf);
          return commitGeometry(geo, "STL");
        }
        if (ext === "obj") {
          const text = await file.text();
          if (requestId !== loadRequestRef.current) return null;
          const obj = new OBJLoader().parse(text);
          // OBJLoader returns a Group of Meshes — merge into one geometry
          const geometries = [];
          obj.traverse((child) => {
            if (child.isMesh && child.geometry) {
              child.updateMatrixWorld();
              const g = child.geometry.clone();
              g.applyMatrix4(child.matrixWorld);
              geometries.push(g);
            }
          });
          if (geometries.length === 0) throw new Error("OBJ file had no geometry.");
          // Simple merge: append all attributes into one BufferGeometry
          const merged = mergeGeometries(geometries);
          geometries.forEach(geometry => geometry.dispose());
          return commitGeometry(merged, "OBJ");
        }
        // Anything else is rejected — STL and OBJ are the only accepted formats.
        setStatus("error");
        onError && onError(`Only STL and OBJ files are accepted (got .${ext}).`);
        return null;
      } catch (err) {
        if (requestId !== loadRequestRef.current) return null;
        setStatus("error");
        onError && onError(err?.message || "Could not parse this file.");
        return null;
      } finally {
        if (requestId === loadRequestRef.current) setLoading(false);
      }
    },
    clear() {
      loadRequestRef.current += 1;
      setLoading(false);
      if (meshRef.current) {
        sceneRef.current.remove(meshRef.current);
        meshRef.current.geometry?.dispose();
        meshRef.current.material?.dispose();
        meshRef.current = null;
      }
      if (tightBoxRef.current) {
        sceneRef.current.remove(tightBoxRef.current);
        tightBoxRef.current.traverse((c) => {
          c.geometry?.dispose?.();
          c.material?.dispose?.();
        });
        tightBoxRef.current = null;
      }
      setStatus("idle");
      setFormat(null);
    },
    // Live-scale the loaded mesh so the viewer reflects the current slider
    // value visually inside the printer bed. Camera position is preserved —
    // if the user has zoomed/panned, respect that. Reframing only happens
    // on initial file load (see commitGeometry).
    setScale(scale) {
      if (!meshRef.current) return;
      meshRef.current.scale.set(scale, scale, scale);
      meshRef.current.position.set(0, 0, 0);
      meshRef.current.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(meshRef.current);
      const centre = box.getCenter(new THREE.Vector3());
      meshRef.current.position.set(-centre.x, -box.min.y, -centre.z);
      // Keep the tight bounding wireframe glued to the scaled mesh.
      if (tightBoxRef.current) {
        tightBoxRef.current.scale.set(scale, scale, scale);
        tightBoxRef.current.position.copy(meshRef.current.position);
      }
    },
    focusModel() {
      const camera = cameraRef.current;
      if (!camera || !meshRef.current) return;
      meshRef.current.updateMatrixWorld(true);
      const sphere = new THREE.Box3().setFromObject(meshRef.current).getBoundingSphere(new THREE.Sphere());
      const vertical = THREE.MathUtils.degToRad(camera.fov);
      const horizontal = 2 * Math.atan(Math.tan(vertical / 2) * camera.aspect);
      const distance = Math.max(1, sphere.radius) / Math.sin(Math.min(vertical, horizontal) / 2) * 1.15;
      const direction = camera.position.clone().sub(controlsRef.current?.target || sphere.center).normalize();
      camera.position.copy(sphere.center).addScaledVector(direction, distance);
      camera.far = Math.max(5000, distance * 20);
      camera.updateProjectionMatrix();
      controlsRef.current?.target.copy(sphere.center);
      camera.lookAt(sphere.center);
      controlsRef.current?.update();
      modelFocusRef.current = true;
    },
    overview() { modelFocusRef.current = false; reframe(null, currentScaleRef.current); },
    // Apply a Euler rotation (radians) to the mesh + tight bounding box.
    // Used by the auto-rotate logic in PrintsQuotePage so the visual model
    // reorients to fit inside the printer envelope when scaling.
    setRotation(rx, ry, rz) {
      if (meshRef.current) {
        meshRef.current.rotation.set(rx || 0, ry || 0, rz || 0);
        meshRef.current.position.set(0, 0, 0);            // reset before re-centering
        meshRef.current.updateMatrixWorld(true);
        // Recompute the world bbox after rotation and shift the mesh so:
        //  • its lowest point sits on the plate (Y = 0)
        //  • its footprint is centered on the bed's X/Z origin
        const wbb = new THREE.Box3().setFromObject(meshRef.current);
        meshRef.current.position.set(
          -(wbb.min.x + wbb.max.x) / 2,
          -wbb.min.y,
          -(wbb.min.z + wbb.max.z) / 2,
        );
      }
      if (tightBoxRef.current) {
        tightBoxRef.current.rotation.set(rx || 0, ry || 0, rz || 0);
        tightBoxRef.current.position.set(0, 0, 0);
        tightBoxRef.current.updateMatrixWorld(true);
        const wbb = new THREE.Box3().setFromObject(tightBoxRef.current);
        tightBoxRef.current.position.set(
          -(wbb.min.x + wbb.max.x) / 2,
          -wbb.min.y,
          -(wbb.min.z + wbb.max.z) / 2,
        );
      }
    },
  };
  useImperativeHandle(ref, () => handleRef.current);

  // Loading belongs to the scene's own lifecycle. In development StrictMode
  // replays this child's mount without replaying its already-mounted parent;
  // a parent-only imperative call can otherwise be cancelled and never retried.
  useEffect(() => {
    if (modelFile) void handleRef.current?.loadFile(modelFile);
  }, [modelFile, bedMm?.x, bedMm?.y, bedMm?.z]);

  return (
    <div className="relative w-full rounded-2xl overflow-hidden border border-white/10 bg-[hsl(222_22%_5%)]" data-testid="stl-viewer" data-state={status} data-format={format || ""}>
      <div ref={mountRef} style={{ height, width: "100%" }} />
      <div className="absolute top-3 right-3 z-10 flex gap-1">
        <button data-testid="stl-focus-model" title="Focus model" aria-label="Focus model" disabled={status !== "loaded"}
          onClick={() => handleRef.current?.focusModel()} className="grid h-9 w-9 place-items-center rounded-md border border-white/20 bg-black/70 text-white hover:bg-white/15 disabled:opacity-30 transition-colors"><Focus className="h-4 w-4" /></button>
        <button data-testid="stl-show-overview" title="Show printer overview" aria-label="Show printer overview" disabled={status !== "loaded"}
          onClick={() => handleRef.current?.overview()} className="grid h-9 w-9 place-items-center rounded-md border border-white/20 bg-black/70 text-white hover:bg-white/15 disabled:opacity-30 transition-colors"><Expand className="h-4 w-4" /></button>
      </div>
      {status === "idle" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Drop a model to preview</div>
            <div className="mt-1 text-xs text-white/30">STL · OBJ — up to 200 MB</div>
          </div>
        </div>
      )}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-300">Loading model…</div>
        </div>
      )}
    </div>
  );
});

/** Minimal geometry merger — enough for OBJ groups. Avoids pulling in the
 *  three-mesh-bvh/BufferGeometryUtils dependency for a one-off case. */
function mergeGeometries(geometries) {
  const positions = [];
  const normals = [];
  for (const g of geometries) {
    const p = g.attributes.position;
    if (!p) continue;
    if (g.index) {
      const idx = g.index.array;
      for (let i = 0; i < idx.length; i++) {
        const j = idx[i] * 3;
        positions.push(p.array[j], p.array[j + 1], p.array[j + 2]);
      }
    } else {
      for (let i = 0; i < p.array.length; i++) positions.push(p.array[i]);
    }
    const n = g.attributes.normal;
    if (n) {
      if (g.index) {
        const idx = g.index.array;
        for (let i = 0; i < idx.length; i++) {
          const j = idx[i] * 3;
          normals.push(n.array[j], n.array[j + 1], n.array[j + 2]);
        }
      } else {
        for (let i = 0; i < n.array.length; i++) normals.push(n.array[i]);
      }
    }
  }
  const merged = new THREE.BufferGeometry();
  merged.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  if (normals.length) merged.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  return merged;
}
